import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, openBlob } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";
import { PatientImportCard } from "@/components/PatientImportCard";

const KINDS = [
  ["facilities", "Facilities", "settings:manage", ["code", "name", "type"]],
  ["providers", "Providers", "settings:manage", ["code", "name", "facility_codes"]],
  ["members", "Members", "member:manage", ["email", "first_name", "last_name", "role", "provider_code", "phone", "password"]],
];
const BADGE = { new: "bg-emerald-100 text-emerald-800", existing_account: "bg-sky-100 text-sky-800", duplicate: "bg-slate-200 text-slate-700",
  error: "bg-red-100 text-red-800", imported: "bg-emerald-600 text-white", failed: "bg-red-600 text-white" };
const LABEL = { new: "New", existing_account: "Existing sign-in - will be added", duplicate: "Duplicate - skipped", error: "Fix in file", imported: "Imported", failed: "Failed" };
const ok = (s) => s === "new" || s === "existing_account";
const show = (v) => (Array.isArray(v) ? v.join(", ") : v === undefined || v === null ? "" : String(v));

function downloadResults(kind, results) {
  const lines = [["row", "record", "status", "temporary password", "notes"], ...results.map((r) => [r.row, r.label, r.status, r.password || "", (r.messages || []).join("; ")])];
  const cell = (c) => { const s = String(c ?? ""); return `"${(/^[=+\-@\t\r]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`; };
  const blob = new Blob([lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ace-${kind}-import-results.csv`;
  a.click();
}

function ReviewTable({ kind, cols, rows, picked, setPicked, results }) {
  const byRow = Object.fromEntries((results || []).map((r) => [r.row, r]));
  return (
    <div className="border rounded-lg overflow-x-auto mt-3" data-testid={`setup-review-${kind}`}>
      <Table>
        <TableHeader><TableRow><TableHead className="w-8" />{["Row", ...cols, "Status"].map((c) => <TableHead key={c} className="capitalize">{c.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r) => {
          const res = byRow[r.row];
          const st = res?.status || r.status;
          return (
            <TableRow key={r.row} data-testid={`setup-row-${kind}-${r.row}`}>
              <TableCell><Checkbox checked={picked.includes(r.row)} disabled={!ok(r.status) || !!results}
                onCheckedChange={(v) => setPicked(v ? [...picked, r.row] : picked.filter((x) => x !== r.row))} data-testid={`setup-pick-${kind}-${r.row}`} /></TableCell>
              <TableCell className="text-xs text-slate-500">{r.row}</TableCell>
              {cols.map((c) => <TableCell key={c} className="text-xs">{show(res?.code && c === "code" ? res.code : r.data[c])}</TableCell>)}
              <TableCell className="text-xs"><span className={`px-2 py-0.5 rounded-full ${BADGE[st]}`} data-testid={`setup-status-${kind}-${r.row}`}>{LABEL[st]}</span>
                {(res?.messages || r.messages).map((m) => <span key={m} className="block text-red-700 mt-0.5">{m}</span>)}</TableCell>
            </TableRow>
          );
        })}</TableBody>
      </Table>
    </div>
  );
}

function KindCard({ kind, title, cols }) {
  const { reloadPractice } = usePractice();
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/setup-import/${kind}/preview`, fd);
      setRows(data.rows); setPicked(data.rows.filter((r) => ok(r.status)).map((r) => r.row)); setResults(null);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/setup-import/${kind}/commit`, { rows: rows.filter((r) => picked.includes(r.row)).map((r) => ({ row: r.row, data: r.data })) });
      setResults(data.results);
      toast.success(`${data.imported} ${title.toLowerCase()} imported`);
      if (kind !== "members") reloadPractice();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border bg-white p-4" data-testid={`setup-card-${kind}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileSpreadsheet className="w-5 h-5 text-brand" /><h3 className="font-semibold mr-auto">{title}</h3>
        <Button size="sm" variant="outline" onClick={() => openBlob(`/setup-import/${kind}/template`, `ace-${kind}-template.xlsx`)} data-testid={`setup-template-${kind}`}><Download className="w-4 h-4 mr-1" />Download template</Button>
        <label className={`inline-flex items-center h-9 px-3 rounded-md text-sm btn-brand cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`setup-upload-${kind}`}>
          <Upload className="w-4 h-4 mr-1" />Upload filled file<input type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} data-testid={`setup-file-${kind}`} /></label>
      </div>
      {rows && <>
        <ReviewTable kind={kind} cols={cols} rows={rows} picked={picked} setPicked={setPicked} results={results} />
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-slate-600 mr-auto" data-testid={`setup-summary-${kind}`}>{rows.length} rows - {picked.length} ticked - {rows.filter((r) => r.status === "duplicate").length} duplicates skipped - {rows.filter((r) => r.status === "error").length} need fixing</span>
          {!results && <Button size="sm" className="btn-brand" disabled={busy || !picked.length} onClick={accept} data-testid={`setup-accept-${kind}`}>Accept & import {picked.length}</Button>}
          {results && kind === "members" && <Button size="sm" variant="outline" onClick={() => downloadResults(kind, results)} data-testid="setup-results-download"><Download className="w-4 h-4 mr-1" />Download results with temporary passwords</Button>}
          {results && <Button size="sm" variant="ghost" onClick={() => setRows(null)} data-testid={`setup-done-${kind}`}>Done</Button>}
        </div>
      </>}
    </section>
  );
}

export default function SetupImport() {
  const { can, scope } = usePractice();
  return (
    <div className="space-y-4" data-testid="setup-import">
      <p className="text-sm text-slate-600 max-w-3xl">Load facilities, providers, staff and patients in bulk. Download a template (it has dropdowns for the allowed values), fill it in, upload it, check the results, then press Accept. Existing records are never duplicated. Import in this order: facilities, providers, members, patients.</p>
      {KINDS.filter(([, , perm]) => can(perm)).map(([k, t, , cols]) => <KindCard key={k} kind={k} title={t} cols={cols} />)}
      {can("settings:manage") && !scope?.limited && <PatientImportCard />}
    </div>
  );
}
